import * as React from 'react'

import {
  Box,
  type Colors,
  Divider,
  EnhancedDimension,
  Icon,
  type SmallIcon,
  useClassNames,
  useToggle,
} from '@factorialco/deprecated-design-system'
import type moment from 'moment'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import DeleteShiftModal from 'modules/shift_management/components/ShiftManagement/DeleteShiftModals/DeleteShiftModal'
import EditShiftSidepanel from 'modules/shift_management/components/ShiftManagement/EditShiftSidepanel'
import SeeShiftSidepanel from 'modules/shift_management/components/ShiftManagement/SeeShiftSidepanel'
import { useColor } from 'modules/shift_management/hooks/useColor'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionDispatch } from 'modules/shift_management/hooks/useSelection'
import {
  trackDeleteOneShiftStart,
  trackEditOneShiftStart,
  trackGoToDetailsShiftStart,
  trackGoToDetailsShiftSubmit,
  trackGoToShiftsContextualMenu,
} from 'modules/shift_management/lib/trackEvents'
import type { Shift, ShiftsRow } from 'modules/shift_management/lib/types'

import * as styles from './index.vanilla.css'

export declare type ViewDimension = EnhancedDimension | 's170'

interface ShiftCellActionsProps {
  shiftsRow: ShiftsRow
  shift: Shift
  selectedDay: moment.Moment
  children: React.ReactNode
  isSelectionEnabled?: boolean
  isSelected?: boolean
  preview: boolean
}

const ShiftCellActions = ({
  shift,
  shiftsRow,
  selectedDay,
  children,
  isSelectionEnabled = false,
  isSelected = false,
  preview,
}: ShiftCellActionsProps) => {
  const [{ view }] = useSchedule()
  const dispatchSelection = useSelectionDispatch()
  const { canManage } = shiftsRow
  const [openEditSidepanel, setOpenEditSidepanel] = React.useState<boolean>(false)
  const [openSeeSidepanel, setOpenSeeSidepanel] = React.useState<boolean>(false)
  const [openDeleteModal, setOpenDeleteModal] = React.useState<boolean>(false)
  const [isHovering, toggleHovering] = useToggle(false)

  const cx = useClassNames(styles)
  const color = useColor(shift)

  const currentSwapShifts = shift.swapShifts
  const requestedSwapShifts = shift.swapShiftsRequests
  const hasSwapShifts = currentSwapShifts.length > 0 || requestedSwapShifts.length > 0
  const hasPendingSwapShifts =
    currentSwapShifts.pending.length > 0 || requestedSwapShifts.pending.length > 0

  const isBackup = shift.isBackup
  const showEmptyBackground = shift.isDraft || hasPendingSwapShifts || (isBackup && preview)
  const background: Colors = showEmptyBackground ? 'grey000White' : color
  const dashed = (hasPendingSwapShifts && !isSelected) || preview
  const align = view.isFullVersion ? undefined : 'center'
  const readOnly = !canManage
  const isAutomatic = shift.isAutomatic
  const showAutomaticIcon =
    shift.isFromUnassigned && isAutomatic && view.isWeekly && view.isByEmployees

  const icon = useMemo((): SmallIcon | null => {
    if (isHovering) return 'ellipsis'
    if (hasSwapShifts) return 'replace'
    if (showAutomaticIcon) return 'ai'

    return null
  }, [isHovering, hasSwapShifts, showAutomaticIcon])

  const onSelect = () => {
    if (readOnly || isBackup) return

    dispatchSelection({
      type: 'TOGGLE_SHIFT',
      shift,
      selectedDay,
      view,
    })
  }

  const onClickMenu = (e: MouseEvent) => {
    trackGoToShiftsContextualMenu(view)
    e.stopPropagation()
  }

  const onSeeShiftAction = () => {
    const from = readOnly ? 'hover' : 'contextual_button'
    trackGoToDetailsShiftStart({ from, view, isBackup })
    toggleHovering(false)()
    setOpenSeeSidepanel(true)
  }

  const onCloseSeeShiftSidepanel = () => {
    const permissions = readOnly ? 'view' : 'edit'
    trackGoToDetailsShiftSubmit(permissions, view)
    setOpenSeeSidepanel(false)
  }

  const onCloseEditShiftSidepanel = () => {
    setOpenEditSidepanel(false)
    dispatchSelection({
      type: 'CLEAR_SELECTION',
    })
  }

  const onOpenEditSidepanel = () => {
    trackEditOneShiftStart(shift, view, 'show_details_modal', shift.typeForEvents)
    setOpenEditSidepanel(true)
    dispatchSelection({
      type: 'TOGGLE_SHIFT',
      shift,
      selectedDay,
      view,
    })
  }

  const onEditAction = () => {
    trackEditOneShiftStart(shift, view, 'contextual_button', shift.typeForEvents)
    toggleHovering(false)()
    setOpenEditSidepanel(true)
    dispatchSelection({
      type: 'TOGGLE_SHIFT',
      shift,
      selectedDay,
      view,
    })
  }

  const onDeleteAction = () => {
    trackDeleteOneShiftStart({ shift, view, from: 'contextual_button', isBackup })
    setOpenDeleteModal(true)
  }

  const borderClass: string[] = useMemo(() => {
    if (isSelected) return ['border.solid.grey800']
    if (dashed) return [`border.dashed.${color}`]
    return [`border.solid.${color}`]
  }, [color, dashed, isSelected])

  return (
    <>
      <Box
        cursor={!readOnly ? 'pointer' : 'default'}
        background={background}
        onClick={onSelect}
        flexDirection="row"
        overflowX="hidden"
        flexGrow
        borderRadius={{ all: 'abs008' }}
        className={cx('relative', borderClass, { preview })}
        justifyContent={align}
        alignItems={align}
      >
        <Box
          overflowX="hidden"
          // paddings -2px to make inner border
          // Y: s6 / left: s8 / right: s8
          paddingY="s4"
          paddingLeft="s6"
          paddingRight={view.isFullVersion ? 's2' : 's6'}
          justifyContent="center"
        >
          {children}
        </Box>

        {isBackup && (
          <Box flexDirection="row" alignItems="flexStart" justifyContent="flexEnd" flexGrow>
            <Box justifyContent="center" padding="s6">
              <Icon.Small icon="history" color="grey600" />
            </Box>
          </Box>
        )}
        <Box
          style={{
            // Not allow actions menu on selection or preview mode
            visibility: isSelectionEnabled || (preview && !isBackup) ? 'hidden' : 'visible',
            right: '0',
            top: '0',
          }}
          flexDirection="row"
          cursor="pointer"
          height={readOnly ? 'full' : undefined} // full only for the see button
          alignItems="flexStart"
          justifyContent="flexEnd"
          position="absolute"
          flexGrow
          gap="s2"
          onMouseEnter={toggleHovering(true)}
          onMouseLeave={toggleHovering(false)}
        >
          {readOnly ? (
            <>
              {!isHovering && hasSwapShifts && (
                <Box justifyContent="center" padding="s6">
                  <Icon.Small icon="replace" color="grey1000" />
                </Box>
              )}
              {isHovering && (
                <Box
                  background="grey800"
                  onClick={onSeeShiftAction}
                  height="full"
                  justifyContent="center"
                  padding="s4"
                >
                  <Icon.Small icon="eye-visible" color="grey000White" />
                </Box>
              )}
            </>
          ) : (
            <Box padding="s4" onClick={(e: MouseEvent) => e.stopPropagation()}>
              <PopoverMenu type="list">
                <PopoverMenu.Trigger>
                  <Box justifyContent="center" padding="s2" onClick={onClickMenu}>
                    {icon ? (
                      <Box background={background}>
                        <Icon.Small icon={icon} color="grey1000" />
                      </Box>
                    ) : (
                      <Box padding="s8" />
                    )}
                  </Box>
                </PopoverMenu.Trigger>

                <PopoverMenu.Content side="bottom" align="end" width="s200">
                  <PopoverMenu.TouchableOption
                    icon="eye-visible"
                    onClick={onSeeShiftAction}
                    title={i18n.t('shift_management.shift_menu_options.show_details')}
                  />
                  {!isBackup && (
                    <>
                      <PopoverMenu.TouchableOption
                        icon="pencil"
                        onClick={onEditAction}
                        title={i18n.t('shift_management.shift_menu_options.edit')}
                      />
                      <Divider></Divider>
                    </>
                  )}
                  <PopoverMenu.TouchableOption
                    icon="delete"
                    onClick={onDeleteAction}
                    title={i18n.t('shift_management.shift_menu_options.delete')}
                    danger
                  />
                </PopoverMenu.Content>
              </PopoverMenu>
            </Box>
          )}
        </Box>
      </Box>

      {openSeeSidepanel && (
        <SeeShiftSidepanel
          shift={shift}
          readOnly={readOnly}
          onClose={onCloseSeeShiftSidepanel}
          onOpenEditSidepanel={onOpenEditSidepanel}
        />
      )}
      {openEditSidepanel && (
        <EditShiftSidepanel shifts={[shift]} onClose={onCloseEditShiftSidepanel} />
      )}

      {openDeleteModal && (
        <DeleteShiftModal
          shift={shift}
          isBackup={isBackup}
          onClose={() => setOpenDeleteModal(false)}
        />
      )}
    </>
  )
}

export default observer(ShiftCellActions)
